import React, {useMemo, useRef} from 'react';
import {classes, style, vars} from './wix-video-resource-view.st.css';
import {
    focalPointResolveHtmlObjectPosition,
    HtmlObjectHorizontalPosition,
    htmlObjectPositionGetHorizontalCssValue,
    htmlObjectPositionGetVerticalCssValue,
    HtmlObjectResizeMode,
    HtmlObjectVerticalPosition,
    OmitStrict,
    sizeCalculateWidthHeightRatio,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import {
    wixMediaResourceResolveMediaWidthHeightRatio,
    WixVideoInfoCustomQualitySelector,
    wixVideoInfoGetVideoUrl,
    WixVideoInfoQualitySelector,
    WixVideoResource
} from "@wix/devzai-common-wix";
import {reactCreateMemo, useElementSizeBinding, useRefsUnion} from "@wix/devzai-utils-react";
import {
    WixMediaViewFocalPoint,
    wixMediaViewFocalPointResolveForWixMediaResource
} from "../wix-media-views-common/wix-media-views-common";

export const WixVideoResourceView = reactCreateMemo(function WixVideoResourceView(props: WixVideoResourceView.Props) {

    const {
        resizeMode = HtmlObjectResizeMode.Contain,
        positionVertical = HtmlObjectVerticalPosition.Top,
        positionHorizontal = HtmlObjectHorizontalPosition.Center,
        videoElementRef,
        src,
        quality,
        showControls = false,
        focalPoint = null,

        style: cssProps,
        className,
        ...htmlAttributes

    } = props;

    const videoSrc = useMemo(() => {
        if (src) {
            return wixVideoInfoGetVideoUrl(src, {
                qualitySelector: quality
            })
        } else {
            return undefined;
        }

    }, [src, quality])

    const evaluatedFocalPoint = src ? wixMediaViewFocalPointResolveForWixMediaResource(focalPoint, src) : null;

    const internalImageElementRef = useRef<HTMLVideoElement>(null);
    const imageViewContainerSize = useElementSizeBinding(internalImageElementRef, evaluatedFocalPoint !== null) ?? null;


    const refsUnion = useRefsUnion([videoElementRef, internalImageElementRef]);
    const videoObjectPosition = evaluatedFocalPoint !== null && resizeMode === HtmlObjectResizeMode.Cover ?
        focalPointResolveHtmlObjectPosition(evaluatedFocalPoint, {
            containerWidthHeightRatio: valueConvertWhenNotNullable(imageViewContainerSize, sizeCalculateWidthHeightRatio),
            objectWidthHeightRatio: valueConvertWhenNotNullable(src, wixMediaResourceResolveMediaWidthHeightRatio) ?? null
        }) :
        {
            positionVertical: positionVertical,
            positionHorizontal: positionHorizontal
        }

    return (
        <video
            ref={refsUnion}
            {...htmlAttributes}
            controls={showControls}
            className={style(classes.root, className)}
            style={{
                [vars.objectFit]: resizeMode,
                [vars.objectPositionVertical]: htmlObjectPositionGetVerticalCssValue(videoObjectPosition),
                [vars.objectPositionHorizontal]: htmlObjectPositionGetHorizontalCssValue(videoObjectPosition),
                ...cssProps
            }}
            src={videoSrc}
        />
    )
});


export namespace WixVideoResourceView {

    export interface Props extends OmitStrict<React.VideoHTMLAttributes<HTMLVideoElement>, 'src'>{
        resizeMode?: HtmlObjectResizeMode;
        positionVertical?: HtmlObjectVerticalPosition | number;
        positionHorizontal?: HtmlObjectHorizontalPosition | number;
        videoElementRef?: React.Ref<HTMLVideoElement>;
        focalPoint?: WixMediaViewFocalPoint;

        src?: WixVideoResource;
        quality?: WixVideoInfoQualitySelector | WixVideoInfoCustomQualitySelector;
        showControls?: boolean;
    }
}